/**
 * NgwEditorLifecycle — `IEditorLifecycle` 의 NgwEditor 구현 (PR-N1 ~ PR-N6).
 *
 * ### 초기화 체인
 *
 * 1. `setEventHandler(handler)` 로 핸들러 보관 (init 보다 선행 권장)
 * 2. `init(opts)` 호출 — host.mode === 'inline' 만 허용
 * 3. 어댑터가 `InlineHost` 생성 → `createRoot(container)` → `<Editor />` 렌더
 * 4. (PR-N6) `handler.getThemeInfo?()` 비동기 조회 → 결과의 isDarkMode 로 `adapter.theme.setDarkMode`
 *    적용 (TinyMCE L5472 평행)
 * 5. NgwEditor `onReady(core)` 발화 → handler.onReady() 호출 + handle.getEditorDOM() 을
 *    InlineHost.setMountElement 로 주입 + `NgwEditorScrollObserver` 부착 → init Promise resolve
 *
 * ### TinyMCELifecycle 와의 차이
 *
 * - iframe / parent.initEditorNative trampoline / nativeCall NineNative 등록이 모두 불필요.
 * - `bindEditorEvents` 거대 함수 (~2,200 라인) 도 없다 — NgwEditor 가 `<Editor onChange/onReady/
 *   onFocus/onBlur/onKeyDown/onPaste/onDrag*>` props 로 이벤트를 노출.
 * - 본 클래스는 PR-N6 에서 31개 콜백 wiring 을 완성한다:
 *     - (a) Editor props 흡수: onReady / onChange (2개 통합) / onFocus / onBlur / onKeyDown /
 *           onPaste / onDragEnter / onDragOver / onDrop / onDragLeave
 *     - (b) PM extension 자체 작성 (`extensions/eventBridge.ts`): copy / cut DOM 이벤트.
 *           selection / format change 비교는 본 클래스가 onChange 시점에 view 상태 비교
 *           (디바운싱 타이머는 인스턴스 멤버 — 사용자 결정 #2).
 *     - (c) Sub-adapter 자체 발화: openImageChooser (NgwEditorImage), notifyNoInput
 *           (NgwEditorState), onScrollChanged / editorHeightChanged (NgwEditorScrollObserver),
 *           showCreateLinkView (NgwEditorState.requestLinkInfo), getThemeInfo (본 클래스 init).
 *     - (d) NodeView/Extension 콜백: clearAvailability / messageHistoryAppended (PR-N5 부터
 *           이미 발화).
 *   PR-N7 까지 stub 유지: 이미지 후처리 4건 (onImageSelected/createProcessedImage/
 *   createProcessedImageList/getProcessedImage) + Rotate/Flip + 수신자·멘션 4건
 *   (onAddRecipients/onRemoveRecipients/closeRecipientsPopup/requestMentionData) +
 *   onPreProcess + showAvailabilityPicker 의 picker 진입. **미발화 영구**: buttonEnabled /
 *   toolbarPropsChanged (toolbar 내장). **hasNativeToolbar**: 어댑터가 'props' 시맨틱이라
 *   핸들러 호출 안 함 (사용자 결정 #6).
 *
 * ### setDefaultStyle 의 key 재마운트 (D-N2)
 *
 * 본 PR 시점에는 setDefaultStyle 호출이 NgwEditorContent.setDefaultStyle 안에서 일어나며,
 * `lifecycle.requestRemount()` 를 통해 본 클래스가 React root 의 `<Editor key={N+1} defaults={...} />`
 * 를 다시 렌더한다. setHTML 으로 보존된 본문은 직전 getHTML 결과를 사용해 동기 보존
 * (undo 히스토리는 의도된 손실 — D-N2 결정).
 */

import { createElement, createRef, type RefAttributes, type RefObject } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import {
  Editor,
  Selection,
  getTableInteractionStore,
  tableInteractionPluginKey,
  type EditorCore,
  type EditorDefaults,
  type EditorHandle,
  type EditorProps,
  type EditorView,
  type Locale,
  type Mark,
  type NgwExtension,
  type Node as ProseMirrorNode,
  type NodeViewFactory,
} from '@ngweditor/react'
import type { IEditorEventHandler } from '../IEditorEventHandler'
import type { IEditorLifecycle } from '../IEditorLifecycle'
import type { FormatChangePayload } from '../IEditorEventHandler'
import type { EditorFilePicker, EditorInitOptions, EditorLocale, ThemeInfo } from '../types'
import './ngwLicense'
// ngwEditor 는 iframe 이 아니라 메인 문서의 contenteditable 에 렌더된다. 편집 본문의 한글·다국어
// 폰트(굴림/맑은 고딕/SimSun 등) @font-face 별칭을 편집기 번들과 함께 실어, 뷰어가 같은 문서에
// 없거나 팝업 작성창(별도 document)이어도 별칭이 보장되게 한다(뷰어의 자체 로드와 상호 보완).
//
// 현재는 fontAliases.css 의 @font-face 별칭 정의 전체가 `⚠️ TEMP DISABLED` 로 주석 처리되어 있어
// (0.4.12 폰트 처리 검증), import 해도 실제 주입되는 내용이 없다. 그래서 이 import 자체도 주석으로
// 둔다. 나중에 fontAliases 별칭을 되살려 편집기 본문에서 다시 필요해지면 아래 import 를 활성화한다.
// 단일 출처는 common-web(utils/ui/fontAliases.css) — 뷰어 사본은 제거됨.
// import 'utils/ui/fontAliases.css'
import { needsLinkClickGuard } from './editorEnv'
import { InlineHost } from './InlineHost'
import { applyNgwToolbarLabelOverrides } from './ngwToolbarLabels'
import type { NgwEditorAdapter } from './NgwEditorAdapter'
import { NgwEditorScrollObserver } from './NgwEditorScrollObserver'
import { createAvailabilityExtension, createAvailabilityNodeView } from './extensions/availability'
import { createDocChangedNotifierExtension } from './extensions/docChangedNotifier'
import { createEventBridgeExtension } from './extensions/eventBridge'
import { createImageDpiAutoSizeExtension } from './extensions/imageDpiAutoSize'
import { createImageSelectionExtension } from './extensions/imageSelection'
import { horizontalRuleNodeView } from './horizontalRuleNodeView'
import {
  createMailRegionsExtension,
  DEFAULT_MAIL_REGION_LABELS,
  REGION_CLASSES,
  type MailRegionLabels,
  createQuotedBodyNodeView,
  createQuotedHeaderNodeView,
  createQuotedToggleButton,
  ensureMailRegionStyles,
  quotedBodyHasContent,
  quotedBodyNodeHasContent,
  type QuotedToggleHandle,
} from './extensions/mailRegions'
import { createMessageHistoryExtension } from './extensions/messageHistory'
import {
  acquireNgwEditorStyles,
  releaseNgwEditorStyles,
  suspendInjectedNgwEditorStyles,
} from './ngwStyleGate'
import { officeNoteExtension } from './extensions/officeNote'
import { removeFormatExtension } from './extensions/removeFormatExtension'
import { sharingExtension } from './extensions/sharing'
import { spanInlineStyleExtension } from './extensions/spanInlineStyle'
import { timeSlotExtension } from './extensions/timeSlot'

// '@ngweditor/react' 모듈 평가가 전역 주입한 에디터 <style>(무스코프 preflight 포함) 를 같은 동기
// 흐름에서 즉시 비활성화한다 — 사이에 렌더링 업데이트가 없어 재계산 비용 0. 대형 메일 뷰어의
// paint 를 지배하던 비용(WebKit ~2.1s/24k 요소)이 사라지고, 에디터 init 에서 다시 활성화된다
// (ngwStyleGate 참고).
suspendInjectedNgwEditorStyles()

/** selection 변경 디바운싱 (TinyMCE 평행 — onSelectionChanged 가 300ms idle 후 발화). */
const SELECTION_CHANGE_DEBOUNCE_MS = 300

/**
 * 에디터 기본 줄간격(line-height) 단일 source of truth.
 *
 * 편집 화면은 라이브러리 `addStyles()` 가 `defaults.lineHeight` 로 `.ngw-editor[data-editor-id]`
 * 에 주입하고(미지정 시 라이브러리 기본값 적용), 송신 HTML 은 NgwEditorContent.buildBodyStyle() 이
 * `<body style>` 에 부착한다. 두 경로가 본 상수를 공유해 편집/송신 줄간격을 일치시킨다.
 */
export const NGW_DEFAULT_LINE_HEIGHT = '1.3'

/**
 * 어댑터가 NgwEditor `<Editor extensions={...} />` prop 에 주입하는 자체 extension 목록 빌더.
 *
 * PR-N3 ~ PR-N6 + 후속 — 현재 12개 (아래 9개 서술 + image-selection + image-dpi-auto-size +
 * doc-changed-notifier):
 *   - `removeFormatExtension` — clear-formatting feature 의 commands 가 empty 라 `executeCommand
 *     ('removeFormat')` 표면 부재 → 어댑터 측에서 동명 명령 등록 (D-N4).
 *   - `mailRegionsExtension` — 4-region (nbody/signature/quoted_header/quoted_body) 노드 +
 *     `insertMailLayout` 명령. legacy TinyMCE 마커 parseDOM 호환. PR-N7 의 feature flag 전환 직후
 *     기존 답장/임시저장 HTML 이 4-region 노드로 정확히 복원되도록.
 *   - `timeSlotExtension` (PR-N5) — `time_slot_space` leaf atom + `insertTimeSlotSpace` 명령.
 *   - `availability` (PR-N5, factory) — `availability` leaf atom + `addSendAvailability` 명령.
 *     factory 형식: NodeView 의 cancel 버튼이 `handler.clearAvailability` 콜백 발화에 어댑터
 *     참조 필요.
 *   - `sharingExtension` (PR-N5) — `sharing_attachments` leaf atom + `addSharingAttachments`
 *     명령 + ul/li 자식 toDOM.
 *   - `officeNoteExtension` (PR-N5) — `office_note` block (content: 'block+') + isHTML 분기.
 *   - `message-history` (PR-N5, factory) — 노드 없음. `triggerShowMessageHistory` 명령이
 *     `handler.messageHistoryAppended` + `quotedButton.triggerInsert` 결합 발화.
 *   - `event-bridge` (PR-N6, factory) — D-N7 결정. NgwEditor 가 props 로 노출하지 않는 표면
 *     (copy/cut DOM 이벤트) 을 PM `view.setProps({ handleDOMEvents })` 로 부착 +
 *     `view` 를 `lifecycle.attachEventBridgeView` 로 전달해 selection/format 비교에 사용
 *     (디바운싱 타이머는 lifecycle 멤버 — 사용자 결정 #2). PreProcess 는 영구 미지원 (사용자
 *     결정 #7).
 */
function buildNgwAdapterExtensions(
  adapter: NgwEditorAdapter,
  nineMailId: string,
  labels: MailRegionLabels,
): NgwExtension[] {
  return [
    removeFormatExtension,
    createMailRegionsExtension(nineMailId, labels),
    timeSlotExtension,
    createAvailabilityExtension(adapter),
    sharingExtension,
    officeNoteExtension,
    createMessageHistoryExtension(adapter),
    createEventBridgeExtension(adapter),
    // editor-image-tools — NodeSelection(image) 감지 → ImageToolbar 트리거.
    createImageSelectionExtension(adapter),
    // editor-image-tools (dpi) — 삽입 직후 PNG 자동 크기를 dpi 로 보정(tinymce setImgMaxSize 재현).
    createImageDpiAutoSizeExtension(adapter),
    // PR-N10 보강 — 외부 메일의 `<span style="text-wrap-mode: nowrap; ...">` 같은 줄바꿈/공백
    // 관련 inline style 이 라이브러리 기본 `textStyle` mark 의 9개 화이트리스트에 매칭되지 않아
    // span 자체가 사라지는 회귀를 보강한다. 화이트리스트는 spanInlineStyle.ts 의 WHITELIST 참고.
    spanInlineStyleExtension,
    // OMD-233 — onChange prop 대체. doc 참조 비교(O(1))로 변경을 감지해 notifyDocChanged 발화.
    createDocChangedNotifierExtension(adapter),
  ]
}

/**
 * 어댑터가 NgwEditor `<Editor nodeViews={...} />` prop 에 주입하는 NodeView 사전 빌더.
 *
 * 현재 등록 목록:
 *   - `quoted_header` / `quoted_body` — 인용 영역 접기/펼치기 NodeView.
 *   - `availability` (PR-N5) — cancel 버튼 NodeView.
 *   - `horizontal_rule` (ngwEditor-migration-0.2.12) — 라이브러리 default toDOM 의
 *     `max-width / margin / width` 자동 prepend 와 CSSOM expand 우회. rawStyle 보존.
 */
function buildNgwAdapterNodeViews(
  adapter: NgwEditorAdapter,
  labels: MailRegionLabels,
): Record<string, NodeViewFactory> {
  return {
    // labels 는 lifecycle 의 live 객체 — setMailRegionLabels 가 제자리(Object.assign) 갱신하므로
    // wrapper 생성 시점에 항상 최신 라벨을 읽는다 (복사본을 넘기면 locale 반영이 끊긴다).
    quoted_header: createQuotedHeaderNodeView(labels),
    quoted_body: createQuotedBodyNodeView(labels),
    availability: createAvailabilityNodeView(adapter),
    horizontal_rule: horizontalRuleNodeView,
  }
}

/**
 * BCP 47 (`EditorLocale`) → NgwEditor 짧은 코드 매핑.
 *
 * pr-n0-inventory §4 D-N1 의 정정 결과: NgwEditor 0.2.10 이 `'ko' | 'en' | 'ja'` 모두
 * 지원하므로 'ja-JP' fallback 이 필요 없다 (05 의 가정 오류 정정).
 */
export const NGW_LOCALE_MAP: Record<EditorLocale, Locale> = {
  'ko-KR': 'ko',
  'ja-JP': 'ja',
  'en-US': 'en',
}

/**
 * `requestRemount` 의 NoOp 가드용 EditorDefaults 동등 비교.
 * fontFamily / fontSize / fontColor 3종 — NgwEditor 의 EditorDefaults 표면 전체.
 */
function areDefaultsEqual(a: EditorDefaults | undefined, b: EditorDefaults | undefined): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  return (
    a.fontFamily === b.fontFamily &&
    a.fontSize === b.fontSize &&
    a.fontColor === b.fontColor &&
    a.lineHeight === b.lineHeight
  )
}

export class NgwEditorLifecycle implements IEditorLifecycle {
  private readonly adapter: NgwEditorAdapter
  /**
   * extensions 배열 — init() 안에서 1회 빌드 후 동일 참조 유지 (referential stability).
   * createMailRegionsExtension 이 nineMailId closure 캡처 필요로 init 까지 발급 지연.
   */
  private extensions: NgwExtension[] = []
  private readonly nodeViews: Record<string, NodeViewFactory>
  private readonly scrollObserver: NgwEditorScrollObserver

  private ready = false
  /** 전역 에디터 <style> 활성 refcount 를 이 인스턴스가 보유 중인지 (acquire/release 짝 보장). */
  private stylesAcquired = false
  private root: Root | null = null
  private readonly handleRef: RefObject<EditorHandle | null> = createRef<EditorHandle>()
  private currentLocale: Locale = 'ko'
  private currentDefaults: EditorDefaults | undefined = undefined
  /**
   * 다크모드 플래그 — renderEditor 가 <Editor theme/contentTheme> 로 반영한다. remount 를 건너
   * 유지되므로(field), setDefaultStyle/setLocale remount 후에도 테마가 보존된다. 갱신은
   * setThemeMode() 로만 (NgwEditorTheme.setDarkMode → 여기로 위임).
   */
  private darkMode = false
  /**
   * 호스트 파일 선택기 — init 옵션으로 받아 renderEditor 가 `<Editor upload={{filePicker}}>` 로
   * forward 한다. app(이메일·서명 편집기)이 주입하며 미지정이면 라이브러리 브라우저 폴백. (OMD-230)
   */
  private filePicker: EditorFilePicker | undefined = undefined
  private remountKey = 0
  private cachedCore: EditorCore | null = null
  private pendingReadyResolve: (() => void) | null = null
  /**
   * requestRemount 가 캡처한 직전 본문 — 새 `<Editor>` 의 onReady(handleEditorReady) 에서 복원한다.
   * `queueMicrotask` 대신 onReady 초크포인트에서 소비하므로 React 18 커밋 타이밍에 의존하지 않는다.
   */
  private pendingRestoreHTML: string | null = null

  /**
   * 본 작성 세션의 nine-mail-id. init() 의 options.nineMailId 로 set 후 onReady 의 nine-mail-id
   * 속성 부여에 사용 (PR-N10 — C5). JMAP emailId 와는 별개 도메인.
   */
  private currentNineMailId: string | null = null

  /** PR-N6 — eventBridge extension 의 setup 콜백에서 전달받은 PM view. */
  private bridgeView: EditorView | null = null
  /** PR-N6 — selection 변경 디바운싱 타이머 (300ms, 사용자 결정 #2 — lifecycle 멤버). */
  private selectionChangeTimer: number | null = null
  private lastSelectionCollapsed: boolean | null = null
  private lastFormatSnapshot: Record<string, boolean | string> = {}

  // 2026-05-19 — paste listener 보유. NgwEditor 0.2.10 의 <Editor onPaste> prop 발화 회귀 우회.
  private pasteListenerTarget: HTMLElement | null = null
  private pasteListener: ((event: ClipboardEvent) => void) | null = null

  // 2026-05-21 — `.ngw-editor` wrapper 의 좌/우 padding 클릭 시 focus 가 PM 으로 이동하지 않는
  // 회귀 우회용 mousedown listener.
  private wrapperClickListenerTarget: HTMLElement | null = null
  private wrapperClickListener: ((event: MouseEvent) => void) | null = null

  // 2026-09-09 — 편집 중 링크 클릭이 브라우저로 열리는 것을 막는 capture 가드
  // (mousedown 에서 target="_blank" 제거 + click 에서 preventDefault). attachLinkClickGuard 참고.
  private linkClickGuardTarget: HTMLElement | null = null
  private linkClickGuardListener: ((event: MouseEvent) => void) | null = null

  // OMD-228 — 에디터/플로팅 메뉴 바깥 클릭 시 selection 해제 capture 리스너.
  private outsideSelectionClearDocs: Document[] | null = null
  private outsideSelectionClearListener: ((event: PointerEvent) => void) | null = null

  private readonly mailRegionLabels = { ...DEFAULT_MAIL_REGION_LABELS }

  setMailRegionLabels(labels: MailRegionLabels = DEFAULT_MAIL_REGION_LABELS): void {
    Object.assign(this.mailRegionLabels, labels)
    this.syncMailRegionLabels()
    this.quotedToggle?.sync()
  }

  /**
   * **이미 렌더된** region 노드에 `role="region"` + `aria-label` 을 현재 라벨로 맞춘다.
   * locale 변경(setMailRegionLabels) 전용 — 초기 라벨은 nbody/signature 는 NodeSpec toDOM,
   * quoted_header/quoted_body 는 NodeView wrapper 생성 시점(createQuoted*NodeView)에 적용된다.
   *
   * ⚠ keystroke 경로(handleEditorChange/ensureQuotedToggle)에서 호출 금지 (OMD-233 잔여 지연
   * 원인이었음). PM 관리 DOM 에 대한 외부 setAttribute 는 DOMObserver 에 foreign mutation 으로
   * 잡혀 readDOMChange 재파싱을 유발한다 — ignoreMutation 이 없는 노드에서는 노드 재생성(라벨
   * 소실)과 재기입이 맞물려 매 키 입력마다 대용량 재파싱 핑퐁이 돈다 (3MB 인용문에서 키당
   * ~100ms 실측).
   *
   * ⚠ `handleRef.current?.` 만으로는 부족하다. `getEditorDOM()` 은 ambient 선언상 non-null 이지만
   * 실제로는 내부에서 `view.dom` 을 읽으므로, **handle 은 이미 붙었고 editor core 는 아직 없는**
   * 구간에서 `Cannot read properties of undefined (reading 'dom')` 로 throw 한다.
   * 라이브러리의 `<Editor>` 가 `createEditorCore()` 직후 handle ref 를 **동기로** 채우는 반면
   * `view` 는 `async initialize()` 의 await 뒤에야 생기기 때문이다.
   *
   * 그 구간은 최초 init 뿐 아니라 **`requestRemount()` 가 새 `<Editor key={N+1}>` 를 띄울
   * 때마다** 다시 열린다. remount 는 `cachedCore` 만 비우고 `ready` 는 내리지 않으므로
   * (`requestRemount` 참고) 그 사이 `isReady()` 는 true 다 — 호출부의 `isReady()` 가드로는
   * 막히지 않는다. 그래서 이 메서드 자체가 조용한 no-op 이어야 한다.
   *
   * 2026-09-13 운영 빌드 실측: 여기서 새어 나간 예외가 React effect 를 타고 올라가 root 전체를
   * 언마운트시켰고, 전역 에러 핸들러가 없는 compose 별도 창은 `#root` 가 빈 채 index.html 의
   * 스켈레톤에서 영구히 멈췄다. 라벨 값은 mailRegionLabels 에 보관되어 remount 후 렌더
   * (toDOM / NodeView 생성)가 live 객체에서 다시 읽으므로 여기서 삼켜도 유실되지 않는다.
   */
  private syncMailRegionLabels(): void {
    let dom: HTMLElement | null
    try {
      dom = this.handleRef.current?.getEditorDOM() ?? null
    } catch {
      return
    }
    if (!dom) return
    for (const key of ['nbody', 'signature', 'quotedHeader', 'quotedBody'] as const) {
      dom.querySelectorAll(`.${REGION_CLASSES[key]}`).forEach((region) => {
        if (region.getAttribute('role') !== 'region') region.setAttribute('role', 'region')
        if (region.getAttribute('aria-label') !== this.mailRegionLabels[key]) {
          region.setAttribute('aria-label', this.mailRegionLabels[key])
        }
      })
    }
  }

  /**
   * "원문 보기 / 숨기기" 토글 버튼. PM doc 에 quoted_body 노드가 존재할 때만 마운트되며
   * `.ngw-editor` wrapper 의 마지막 자식으로 부착된다. 부재 시 미마운트 (조건부 생성).
   * 본 멤버는 wrapper 와 handle 의 lifecycle 안에서만 유효.
   */
  private quotedToggle: QuotedToggleHandle | null = null
  private quotedToggleWrapper: HTMLElement | null = null

  /**
   * quotedBodyHasContent 결과의 노드 참조 캐시 (OMD-233 후속). 판정이 3MB 급 인용문의
   * `textContent` 전체 읽기 + `trim()` 사본 할당이라 키 입력당 ~4.6ms + GC 를 차지했다.
   * PM doc 은 불변 — quoted_body 노드 참조가 같으면 내용도 같으므로 참조 비교(O(1))로 재사용.
   */
  private quotedBodyContentCache: { node: ProseMirrorNode; hasContent: boolean } | null = null

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
    // extensions 는 init() 안에서 nineMailId 결정 후 1회 빌드 — referential stability 보장.
    // mailRegionLabels 는 필드 선언(=생성자 body 이전) 시점에 초기화되어 안전하게 전달 가능.
    this.nodeViews = buildNgwAdapterNodeViews(adapter, this.mailRegionLabels)
    this.scrollObserver = new NgwEditorScrollObserver(adapter)
  }

  setEventHandler(handler: IEditorEventHandler): void {
    this.adapter.setHandlerInternal(handler)
  }

  isReady(): boolean {
    return this.ready
  }

  /**
   * 동적 locale 변경. NgwEditor 의 `<Editor locale={...} />` locale prop 은 마운트 시점에만
   * 캡처되는 초기값이라, prop 만 갱신해서는 툴바 언어가 바뀌지 않는다. 따라서 setDefaultStyle
   * 과 동일하게 본문 보존 후 key 재마운트(`requestRemount`)로 언어를 반영한다 (undo 손실은
   * D-N2 결정과 동일하게 의식적 허용).
   *
   * init 전(root == null) 호출 시 다음 init 에서 사용할 currentLocale 만 저장 — 첫 마운트를
   * 올바른 언어로 렌더하게 한다.
   *
   * 동일 locale 재호출은 NoOp — EmailForm.onReady 가 매 onReady 발화마다 `applyLanguage` 를
   * 호출하는데, 가드 없이 재마운트하면 `onReady → applyLanguage → setLocale → 재마운트` 무한
   * 루프가 발생한다. NoOp 가드가 재마운트로 재발화된 onReady 의 setLocale(동일값)을 흡수한다.
   */
  setLocale(locale: EditorLocale): void {
    const mapped = NGW_LOCALE_MAP[locale]
    if (this.currentLocale === mapped) {
      return
    }
    this.currentLocale = mapped
    if (this.root == null) {
      return
    }
    // 본문 보존 + 복원은 requestRemount 가 일괄 처리 (handleEditorReady 에서 소비).
    this.requestRemount()
  }

  async init(options: EditorInitOptions): Promise<void> {
    if (options.host.mode !== 'inline') {
      // NgwEditor 는 inline 마운트 전용 (05 §4.1).
      throw new Error(
        `NgwEditorLifecycle.init: host.mode '${options.host.mode}' not supported (only 'inline')`,
      )
    }

    if (!options.nineMailId) {
      throw new Error(
        'NgwEditorLifecycle.init: nineMailId is required for NgwEditor. ' +
          'buildEditorInitOptions(..., nineMailId) 로 전달하세요.',
      )
    }
    this.currentNineMailId = options.nineMailId
    // 호스트 파일 선택기 — renderEditor 가 <Editor upload={{filePicker}}> 로 forward. 미지정이면
    // 라이브러리가 브라우저 input 폴백(web 기본).
    this.filePicker = options.filePicker
    // 모듈 평가 때 비활성화해 둔 전역 에디터 <style> 를 에디터가 실제로 뜨는 동안만 활성화한다.
    // dispose 의 release 와 짝 — stylesAcquired 로 중복 dispose 시 이중 release 를 막는다.
    if (!this.stylesAcquired) {
      this.stylesAcquired = true
      acquireNgwEditorStyles()
    }
    // 4-region CSS (fold/expand 토글) 주입은 handleEditorReady(onReady) 로 이동했다. 라이브러리는
    // EditorCore.start 에서 `ngw-editor-core-styles` 를 head 에 append 한 **직후** onReady 를
    // 발화하므로, onReady 시점에 mailRegionStyles 를 append 해야 head 내 순서가 라이브러리 default
    // 뒤에 놓여 동일 specificity 규칙을 cascade 로 override 할 수 있다 (init 단계 주입은 라이브러리
    // 마운트 전이라 core-styles 가 나중에 들어와 우리 규칙을 덮는다).
    // 구분선(lineset) 4종 노출에 맞춰 dotted 드롭다운 라벨을 "구분선4" 로 재번호 (OMD-46, 멱등).
    applyNgwToolbarLabelOverrides()
    // extensions 배열은 본 init() 안 1회만 빌드 — render 마다 새 배열을 만들면 NgwEditor 가
    // schema 재구성을 트리거할 수 있다 (referential stability).
    this.extensions = buildNgwAdapterExtensions(
      this.adapter,
      options.nineMailId,
      this.mailRegionLabels,
    )

    const host = new InlineHost({ container: options.host.container })
    this.adapter.setHost(host)

    if (options.locale != null) {
      this.currentLocale = NGW_LOCALE_MAP[options.locale]
    }
    if (options.defaultStyle != null) {
      // EditorDefaultStyle (camelCase) → EditorDefaults 매핑. NgwEditor 의 defaults 는
      // fontFamily / fontSize / fontColor / backgroundColor / lineHeight 5종. lineHeight 는
      // 호스트 입력(EditorDefaultStyle)에 없어 NGW_DEFAULT_LINE_HEIGHT 로 명시 전달한다 — 미전달
      // 시 라이브러리 기본값(편집)과 송신 HTML 의 buildBodyStyle 값이 불일치하기 때문.
      this.currentDefaults = {
        fontFamily: options.defaultStyle.fontFamily,
        fontSize: options.defaultStyle.fontSize,
        fontColor: options.defaultStyle.color,
        lineHeight: NGW_DEFAULT_LINE_HEIGHT,
      }
    }

    this.root = createRoot(options.host.container)

    // PR-N6 — handler.getThemeInfo 비동기 조회 후 theme.setDarkMode 적용 (TinyMCE L5472 평행,
    // 사용자 결정 #5). init Promise awaitable 가 1단계 늘어나지만 parent 가 isDarkMode 를 핸들러
    // 콜백으로 제어할 수 있는 자유도 확보. 핸들러 미구현 시 options.isDarkMode fallback.
    const themeInfo = await this.resolveThemeInfo(options.isDarkMode)
    this.adapter.theme.setDarkMode(themeInfo.isDarkMode)

    // onReady 발화까지 대기. 본 Promise 는 props.onReady 안에서 resolve.
    await new Promise<void>((resolve) => {
      this.pendingReadyResolve = resolve
      this.renderEditor()
    })

    this.ready = true
  }

  /**
   * 핸들러의 getThemeInfo 가 있으면 비동기 조회, 없으면 options.isDarkMode 로 fallback.
   * TinyMCE 평행: nine_tinymce_desktop.js L5472 의 `nativeCall('getThemeInfo', callback)` 가
   * 부재 시 false 로 호출되는 흐름과 동일 시맨틱.
   */
  private async resolveThemeInfo(fallbackIsDarkMode: boolean | undefined): Promise<ThemeInfo> {
    const handler = this.adapter.getHandler()
    if (handler?.getThemeInfo == null) {
      return { isDarkMode: fallbackIsDarkMode === true }
    }
    try {
      return await handler.getThemeInfo()
    } catch {
      // 핸들러 throw 는 silent fallback — init 자체를 깨뜨리지 않는다.
      return { isDarkMode: fallbackIsDarkMode === true }
    }
  }

  dispose(): void {
    this.ready = false
    // 전역 에디터 <style> 활성 refcount 반납 (init 의 acquire 와 짝, ngwStyleGate 참고).
    if (this.stylesAcquired) {
      this.stylesAcquired = false
      releaseNgwEditorStyles()
    }
    // editor-image-tools (SE10) — cachedCore=null 전에 선택 상태/픽셀화 캐시 정리 →
    // ImageToolbar stale flash·portal 누수 방지.
    this.adapter.image.setSelectedImage(null)
    this.adapter.image.clearPixelizeCache()
    this.cachedCore = null
    this.pendingReadyResolve = null

    // noInput 타이머는 caller(EmailForm/SignatureEdit) cleanup 에 의존하지 않고 dispose 에서
    // 직접 정리한다(특히 SignatureEdit 의 legacy iframe 가드로 ngw 인라인 모드에서 미정리되던 누수).
    this.adapter.state.clearNoInputTimer()

    // PR-N6 — selection 디바운싱 타이머 cleanup (사용자 결정 #2: lifecycle 멤버 일괄 정리).
    if (this.selectionChangeTimer != null) {
      const win = this.getSafeWindow()
      if (win != null) {
        win.clearTimeout(this.selectionChangeTimer)
      }
      this.selectionChangeTimer = null
    }

    // PR-N6 — scroll/height observer detach (listener removeEventListener + ResizeObserver
    // disconnect 모두 수행).
    this.scrollObserver.detach()

    // 2026-05-19 — paste listener 해제.
    this.detachPasteListener()

    // 2026-05-21 — wrapper click listener 해제.
    this.detachWrapperClickListener()

    // 2026-09-09 — 링크 클릭 가드 해제.
    this.detachLinkClickGuard()

    // OMD-228 — 바깥 클릭 selection 해제 listener 해제.
    this.detachOutsideSelectionClear()

    // 원문 보기/숨기기 토글 — 부착되어 있으면 listener cleanup + DOM 분리.
    if (this.quotedToggle != null) {
      this.quotedToggle.cleanup()
      this.quotedToggle = null
      this.quotedToggleWrapper = null
    }

    this.bridgeView = null
    this.lastSelectionCollapsed = null
    this.lastFormatSnapshot = {}
    // OMD-233 후속 — hasContent 캐시가 이전 doc 의 quoted_body 노드(대용량 서브트리)를 붙잡지
    // 않도록 해제.
    this.quotedBodyContentCache = null

    // 어댑터가 보유한 blob URL 누수 방지 — root.unmount 직전에 모두 revoke.
    this.adapter.image.disposeBlobs()
    // PR-N9a-1 — data URL → 원본 src 역치환 registry 도 함께 비운다.
    this.adapter.image.disposeUrlMap()
    // PR-N9a-2 보강 — dataUrl → 생성된 cid 캐시도 비운다.
    this.adapter.image.disposeDataUrlCidMap()

    if (this.root != null) {
      // React 18 의 Root.unmount() 를 render 중 동기 호출하면 "Attempted to synchronously
      // unmount a root while React was already rendering" warning 이 발생한다.
      // EmailForm.componentWillUnmount 가 호출하는 경로 (MainDef.removeEditItem → setState
      // → Tabs panel 제거 → EmailForm unmount) 가 정확히 이 시점이라 microtask 로 defer.
      const rootToUnmount = this.root
      this.root = null
      queueMicrotask(() => {
        try {
          rootToUnmount.unmount()
        } catch {
          // 이미 unmount 되었거나 container 가 detach 된 상태 — silent.
        }
      })
    }

    try {
      this.adapter.host.teardown()
    } catch {
      // host 가 이미 정리되었거나 init 전 dispose — silent.
    }

    this.adapter.clearHost()
    this.adapter.setHandlerInternal(null)
  }

  /**
   * 어댑터 내부에서 호출 — setDefaultStyle / setLocale 등이 key 재마운트를 요청.
   * D-N2 결정: undo 손실 의식적. 직전 본문은 getHTML 으로 캡처해 `pendingRestoreHTML` 에 저장하고,
   * 새 `<Editor>` 의 onReady(handleEditorReady) 에서 setHTML 으로 복원한다 — 복원 시점을 onReady
   * 초크포인트에 묶어 React 18 커밋 타이밍(root.render 스케줄링)에 의존하지 않게 한다.
   *
   * `nextDefaults` 가 현재 `currentDefaults` 와 동일하면 NoOp — EmailForm 의 새 메일 흐름이
   * 매 onReady 발화마다 `applyEditorSettings()` → `content.setDefaultStyle(...)` 를 호출하는데
   * 가드 없이 remountKey 를 증가시키면 `<Editor key={N+1} />` 가 재마운트되어 onReady 가 다시
   * 발화 → 무한 루프가 발생한다.
   * @internal
   */
  requestRemount(nextDefaults?: EditorDefaults): void {
    if (nextDefaults != null) {
      if (areDefaultsEqual(this.currentDefaults, nextDefaults)) {
        return
      }
      this.currentDefaults = nextDefaults
    }
    // remount 로 새 EditorState 가 빈 본문으로 시작하므로, 직전 본문을 캡처해 onReady 에서 복원.
    const preserved = this.getHandle()?.getHTML({ includeStyles: true }) ?? ''
    this.pendingRestoreHTML = preserved.length > 0 ? preserved : null
    // editor-image-tools (SE10) — remount 전 선택 상태 해제(새 view 로 stale pos 무효화).
    this.adapter.image.setSelectedImage(null)
    // remount 동안 old core/bridgeView 는 파괴되므로 stale 참조를 비운다(새 onReady/eventBridge
    // setup 이 재설정). getCore()/handleEditorChange 가 null 을 안전 처리.
    this.cachedCore = null
    this.bridgeView = null
    this.remountKey += 1
    if (this.root != null) {
      this.renderEditor()
    }
  }

  /**
   * 다크모드 갱신 — NgwEditorTheme.setDarkMode 가 위임한다. 플래그만 바꾸고 renderEditor 를
   * **같은 remountKey 로** 재호출해 <Editor theme/contentTheme> 를 reactive 갱신한다(requestRemount
   * 와 달리 본문 캡처·remount·onReady 재발화 없음 — 0.4.17 useResolvedTheme 로 검증).
   *
   * init 단계(cachedCore == null, 첫 renderEditor 전)에는 저장만 한다 — 이어지는 초기 renderEditor
   * 가 값을 읽어 첫 프레임부터 반영하므로 FOUC 가 없고, ready 전 렌더로 pendingReadyResolve 를
   * 조기 소모하는 버그도 피한다.
   * @internal
   */
  setThemeMode(isDark: boolean): void {
    if (this.darkMode === isDark) {
      return
    }
    this.darkMode = isDark
    if (this.cachedCore != null) {
      this.renderEditor()
    }
  }

  /** 어댑터 내부에서 호출 — onReady 콜백이 받은 core 인스턴스 노출. @internal */
  getCore(): EditorCore | null {
    return this.cachedCore
  }

  /** 어댑터 내부에서 호출 — EditorHandle ref. @internal */
  getHandle(): EditorHandle | null {
    return this.handleRef.current
  }

  /** 어댑터 내부에서 호출 — init 시점에 set 된 본 작성 세션의 nine-mail-id. @internal */
  getNineMailId(): string | null {
    return this.currentNineMailId
  }

  /** 어댑터 내부에서 호출 — setDefaultStyle 로 적용된 fontFamily/fontSize/fontColor. @internal */
  getDefaults(): EditorDefaults | undefined {
    return this.currentDefaults
  }

  /**
   * 어댑터 내부에서 호출 — eventBridge extension 의 setup 콜백이 전달한 PM view (PR-N6).
   * selection 조회 / mark 비교 등에 사용.
   * @internal
   */
  getBridgeView(): EditorView | null {
    return this.bridgeView
  }

  /**
   * 현재 보유한 옵션으로 `<Editor />` 를 React root 에 (재)렌더한다.
   * onReady 발화 시 핸들러 forwarding + InlineHost mount element 주입 + ScrollObserver 부착.
   * 그 외 7개 props 는 IEditorEventHandler 콜백 forwarding (PR-N6).
   */
  private renderEditor(): void {
    if (this.root == null) {
      return
    }

    const props: EditorProps & RefAttributes<EditorHandle> & { key: number } = {
      key: this.remountKey,
      ref: this.handleRef,
      preset: 'full',
      // ngwEditor 기본 toolbar 버튼 목록(defaultEditorConfig.toolbar.buttons)에서 파일 첨부
      // 버튼('filelink')만 제외한 구성. preset 을 지우고 features 로 file-links 를 빼는 방식은
      // defaultEditorConfig 의 preset:'full' 이 deepMerge 로 다시 채워져 무효화되므로, toolbar
      // 버튼 목록을 직접 지정한다 (toolbar prop 은 deepMerge 를 거치지 않고 렌더에 직접 반영됨).
      // 메일 첨부는 EmailForm 의 별도 첨부 UI 를 사용한다.
      toolbar: {
        buttons: [
          'undo',
          'redo',
          '|',
          'formatblock',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'fontname',
          'fontsize',
          '|',
          'fontcolor',
          'fontbgcolor',
          '|',
          'clearformatting',
          // '-',
          'lineheight',
          'textalign',
          '|',
          'indent',
          '|',
          'bulletlist',
          'orderedlist',
          '|',
          // 인용구: 버티컬라인 단일 (variants 1개 → 드롭다운 없는 단일 버튼). 이메일에서 안전한
          // 스타일만 노출하고 가상요소 의존 7종의 UI 진입을 차단 (OMD-46, 0.2.26 variants API).
          { type: 'blockquote', variants: ['vertical'] },
          // 구분선: 이메일 안전 스타일만 노출 (OMD-46). wavy/diamond 는 SVG mask 라 외부 메일에서
          // mask 제거 시 색 블록으로 깨지므로 제외. dotted(gradient)는 미지원 시 사라질 뿐이라 허용.
          { type: 'lineset', variants: ['solid', 'solid-thin', 'solid-thick', 'dotted'] },
          'pagebreak',
          '|',
          'link',
          'table',
          'image',
          // video 입력 시 처리가 현재 메일 발송 데이터 구조와 맞지 않아 임시로 비활성화
          // 'video',
          'search',
        ],
      },
      locale: this.currentLocale,
      // 0.4.17 네이티브 다크모드 — UI 크롬(theme) + 본문(contentTheme) 모두. 기본 light,
      // darkMode 만 dark. useResolvedTheme 로 reactive 라 같은 remountKey 재렌더 시 remount·onReady
      // 재발화 없이 data-ngw-theme 속성만 갱신된다. getHTML()·인라인 style 은 불변(발송 안전).
      theme: this.darkMode ? 'dark' : 'light',
      contentTheme: this.darkMode ? 'dark' : 'light',
      defaults: this.currentDefaults,
      // 줄간격 드롭다운 옵션. 라이브러리 기본 7종(1.0/1.2/1.4/1.5/1.6/1.8/2.0)에 1.2 다음 1.3 을
      // 추가(NGW_DEFAULT_LINE_HEIGHT 기본값과 동일 — 본문 기본 줄간격을 드롭다운에서도 선택 가능하게).
      // formatting.lineHeights 지정 시 전체 목록을 override 하므로 기존 값을 모두 포함해야 한다.
      formatting: { lineHeights: ['1.0', '1.2', '1.3', '1.4', '1.5', '1.6', '1.8', '2.0'] },
      layout: { height: '100%' },
      // 내장 이미지 퀵메뉴(0.4.x) 비활성화. 어댑터가 image-selection 확장으로 자체 ImageToolbar 를
      // 띄우므로, 라이브러리 기본 ImageQuickMenu 와 이중 노출되는 것을 막는다. (다른 퀵메뉴는 기본 유지)
      quickMenu: { image: false },
      // 호스트 파일 선택기(0.4.18 upload.filePicker). app 이 주입했으면 모든 파일 선택점이 이걸 탄다
      // (desktop: Tauri plugin-dialog 확장자 필터). 미지정이면 upload 미전달 → 라이브러리 브라우저 폴백.
      upload: this.filePicker ? { filePicker: this.filePicker } : undefined,
      extensions: this.extensions,
      nodeViews: this.nodeViews,
      // PR-N10 (0.2.11) — 가이드 §6. 인용 본문의 inline style / table 속성 / colgroup / 외부
      // HTML 멱등성 보존. ParseDOM unknown attribute drop 정책을 완화하여 외부 메일 (Outlook /
      // Gmail / Jira) 답장 라운드트립 시 시각 회귀 방지.
      preserveHtml: true,
      onReady: (core) => this.handleEditorReady(core),
      // onChange prop 은 의도적으로 전달하지 않는다 (OMD-233). onChange 가 설정되어 있으면
      // 라이브러리 handleTransaction 이 매 docChanged 트랜잭션마다 getHTML()(전체 문서
      // DOMSerializer 직렬화) + doc.toJSON() 을 eager 계산해 대용량 본문(2MB+)에서 키 입력당
      // ~1.15초가 소모된다. 어댑터는 payload 를 사용하지 않으므로 변경 감지는
      // doc-changed-notifier extension 의 plugin update(doc 참조 비교) → notifyDocChanged 로 대체.
      onFocus: () => this.adapter.getHandler()?.onEditorFocus?.(),
      onBlur: () => this.adapter.getHandler()?.onEditorBlur?.(),
      onKeyDown: (event) => this.adapter.getHandler()?.onEditorKeyDown?.(event),
      onPaste: (event) => this.adapter.getHandler()?.onEditorPaste?.(event),
      onDragEnter: (event) => this.adapter.getHandler()?.onEditorDragEnter?.(event),
      onDragOver: (event) => this.adapter.getHandler()?.onEditorDragOver?.(event),
      onDrop: (event) => this.adapter.getHandler()?.onEditorDrop?.(event),
      onDragLeave: (event) => this.adapter.getHandler()?.onEditorDragLeave?.(event),
    }

    // `hasNativeToolbar` 는 어댑터가 'props' 시맨틱이라 핸들러 호출 자체를 하지 않는다 (사용자
    // 결정 #6). TinyMCE 어댑터에서만 nine_tinymce_desktop.js L5441 의 nativeCall 경로로 호출.

    this.root.render(createElement(Editor, props))
  }

  /**
   * onReady 콜백 본체. core 보관 + InlineHost mount + ScrollObserver 부착 + handler.onReady 발화 +
   * pendingReadyResolve. selection/format 비교용 초기 스냅샷도 본 시점에 기록.
   *
   * `this.ready = true` 는 handler.onReady() 호출 직전에 set 한다 — EmailForm.onReady 가 첫 줄에서
   * `lifecycle.isReady() !== true` 가드로 early return 하기 때문 (TinyMCE 평행: TinyMCELifecycle 는
   * onReady 가 setTimeout 폴링으로 deferred 되어 init() 의 await 가 먼저 완료되지만, NgwEditor 는
   * EditorCore.start 내부에서 동기적으로 onReady 가 발화되므로 명시적 선행 set 이 필요하다).
   * init() 의 마지막 줄도 그대로 `this.ready = true` 를 유지 — 멱등.
   */
  private handleEditorReady(core: EditorCore): void {
    this.cachedCore = core
    // 4-region CSS (fold/expand 토글) 를 document.head 에 멱등 주입. NodeView 의 토글 버튼이 켜는
    // `.fold` 클래스를 자식 content 숨김 규칙으로 해석하는 단일 출처. 라이브러리가 EditorCore.start
    // 에서 `ngw-editor-core-styles` 를 append 한 직후(onReady) 호출되므로, 본 style 이 head 내 라이브러리
    // default 뒤에 놓여 동일 specificity 규칙을 cascade override 한다.
    ensureMailRegionStyles()
    const handler = this.adapter.getHandler()
    const handle = this.handleRef.current
    const dom = handle?.getEditorDOM()
    if (dom != null) {
      const host = this.adapter.getHostInternal()
      if (host != null) {
        host.setMountElement(dom)
      }
      // PR-N6 — scroll/height 관찰자 부착. dispose 시 detach.
      this.scrollObserver.attach(dom)

      // 2026-05-19 — paste listener 직접 등록 (capture phase). NgwEditor 0.2.10 의 <Editor>
      // 컴포넌트가 onPaste prop 을 React 차원에서 발화하지 않는 회귀를 우회. handler 의
      // onEditorPaste 가 호출되도록 DOM 차원의 paste 이벤트를 직접 가로채서 forward.
      this.attachPasteListener(dom)

      // 2026-05-21 — `.ngw-editor` wrapper padding 클릭 시 focus 보강.
      this.attachWrapperClickListener(dom)

      // 2026-09-09 — 편집기 내 링크 클릭 시 기본 네비게이션(브라우저 열림) 차단.
      // Host capability 로 gate: platform(Tauri/wry) 만 필요, web 은 attach 하지 않는다(editorEnv).
      if (needsLinkClickGuard()) {
        this.attachLinkClickGuard(dom)
      }

      // OMD-228 — 에디터/플로팅 메뉴 바깥 클릭 시 selection 해제. 이미지 NodeSelection·표
      // CellSelection 이 풀리면 그에 딸린 ngweditor quick menu·selection 하이라이트·커스텀
      // ImageToolbar(imageSelection→setSelectedImage(null))가 모두 자동으로 닫힌다.
      this.attachOutsideSelectionClear(dom)
    }

    // PR-N10 (0.2.11) — 루트 컨테이너에 nine-mail-id 속성 부여. handle.container 의
    // `.ngw-editor-container` 안쪽 (NgwEditor 마운트 표면) 에 설정. 가이드 §8 의 4-region 루트
    // 식별자 보존을 위한 명시적 부착.
    if (this.currentNineMailId != null) {
      const container = handle?.container
      if (container != null) {
        const inner = container.querySelector('.ngw-editor-container') ?? container
        inner.setAttribute('nine-mail-id', this.currentNineMailId)
      }
    }

    // PR-N6 — bridgeView 가 setup 콜백에서 이미 전달됐다면 selection/format 초기 스냅샷 기록.
    // setup 이 onReady 보다 늦게 호출되는 환경 (test 등) 에서는 첫 onChange 시점에 기록.
    if (this.bridgeView != null) {
      this.lastSelectionCollapsed = this.bridgeView.state.selection.empty
      this.lastFormatSnapshot = this.captureFormatSnapshot(this.bridgeView)
    }

    this.ready = true
    handler?.onReady?.()
    if (this.pendingReadyResolve != null) {
      this.pendingReadyResolve()
      this.pendingReadyResolve = null
    }

    // 원문 보기/숨기기 토글 — quoted_body 존재 시 `.ngw-editor` wrapper 끝에 부착.
    // 본 시점에는 PM doc 에 region 노드가 없을 가능성이 높지만, EmailForm.onReady 가 비동기로
    // quotedBody.set 을 호출하므로 후속 handleEditorChange 에서 ensureQuotedToggle 이 다시 호출.
    this.ensureQuotedToggle()

    // requestRemount 로 재마운트된 경우 직전 본문 복원. handler.onReady 이후에 실행하여 EmailForm.
    // onReady 의 초기화(빈 nBody 주입 등)를 덮어쓴다 — 기존 queueMicrotask 순서와 동일하되, 새
    // 핸들이 확실히 준비된 onReady 시점이라 타이밍 경합이 없다. setHTML 의 docChanged 트랜잭션이
    // doc-changed-notifier → handleEditorChange 로 ensureQuotedToggle 을 다시 트리거해 인용
    // 토글도 복원된다 (OMD-233 — 구 onChange 경로와 동일 동작).
    if (this.pendingRestoreHTML != null) {
      const html = this.pendingRestoreHTML
      this.pendingRestoreHTML = null
      this.handleRef.current?.setHTML(html)
    }
  }

  /**
   * doc-changed-notifier extension 이 doc 변경을 감지했을 때 호출하는 진입점 (OMD-233).
   * `<Editor onChange>` prop 전달 시 라이브러리가 매 docChanged 마다 전체 문서 직렬화
   * (getHTML + toJSON) 를 수행하므로, onChange prop 대신 본 메서드가 변경 신호를 받는다.
   * 발화 조건(plugin update 의 doc 참조 비교)은 기존 onChange 의 `transaction.docChanged`
   * 게이트와 동치 — 상세는 extensions/docChangedNotifier.ts 헤더 주석 참조.
   * @internal
   */
  notifyDocChanged(): void {
    this.handleEditorChange()
  }

  /**
   * doc 변경 콜백 본체 (구 onChange 콜백 — OMD-233 이후 notifyDocChanged 경유로만 호출).
   * PR-N1 의 dirty 마킹 / onInputChanged / onContentChanged 에 더해 PR-N6 의
   * selection (디바운싱) / format (즉시) 비교를 수행한다.
   *
   * **본문 입력 시 noInput 타이머 재시작 (2026-05-19 보강)**: TinyMCE 평행에서는 editor input
   * 이벤트가 자동으로 `editorMgr.checkNoInput` 을 호출하여 자동 저장 디바운싱을 시작했다.
   * NgwEditor 어댑터에서는 본 onChange 가 동등 역할 — 본문이 바뀔 때마다 `state.checkNoInput()`
   * 호출하여 `notifyNoInput` 디바운싱 타이머를 재시작. EmailForm 의 `setNoInputTime(10000)` 가
   * onReady 직후 호출되어 noInputTimeMs 가 10초로 세팅되어 있으면 10초 무입력 후 자동 저장.
   */
  private handleEditorChange(): void {
    this.adapter.markDirty()
    // editor-image-tools — 문서 편집 시 전송 픽셀화 캐시 무효화(전송 후 재편집 시 stale strip 방지).
    this.adapter.image.clearPixelizeCache()
    const handler = this.adapter.getHandler()
    handler?.onInputChanged?.()
    handler?.onContentChanged?.()

    // 본문 입력 시 noInput 타이머 디바운싱 재시작 — 자동 저장 트리거.
    this.adapter.state.checkNoInput()

    // quoted_body 존재 여부 / collapsed 상태에 따라 토글 버튼 마운트·언마운트·sync.
    this.ensureQuotedToggle()

    // PR-N6 — bridgeView 있을 때만 selection/format 비교. setup 이 onReady 보다 늦게 호출된 환경에서도
    // 동작.
    if (this.bridgeView == null) {
      return
    }
    this.scheduleSelectionChangedCheck()
    this.detectFormatChanged()
  }

  /**
   * `.ngw-editor` wrapper 끝의 "원문 보기/숨기기" 토글 버튼을 PM doc 의 quoted_body 노드 존재
   * 여부에 따라 조건부로 마운트/언마운트.
   *
   *   - quoted_body 있음 + 버튼 없음 → 새로 부착
   *   - quoted_body 없음 + 버튼 있음 → cleanup 후 제거
   *   - 둘 다 있음 → sync() 로 라벨/표시 상태만 갱신
   *
   * onReady 직후 + 매 onChange 시점에 호출. 호출 비용은 doc descendants 1회 순회 + classList
   * 토글 정도라 부담 없음.
   */
  private ensureQuotedToggle(): void {
    const core = this.cachedCore
    const handle = this.handleRef.current
    if (core == null || handle == null) return

    const editorDom = handle.getEditorDOM()
    const wrapper = editorDom?.parentElement ?? null
    if (wrapper == null || !wrapper.classList.contains('ngw-editor')) {
      return
    }

    // quoted_body 존재만으로 토글을 걸지 않고, 실제 인용 콘텐츠가 있는지까지 본다 — 새 메일에서
    // 편집 중 lazy 조립되거나 임시보관 후 재로딩된 **빈** quoted_body 는 "..."(원문 보기) phantom
    // 토글의 원인이므로 그때는 토글을 걸지 않는다. NodeView wrapper(`.quoted_body_editor`)를 직접
    // 읽어 media 포함까지 보는 quotedBodyHasContent 로 판정한다(collapsed=fold 로 display:none 이어도
    // textContent/querySelector 는 정상 동작). 판정 결과는 노드 참조 캐시로 재사용 (OMD-233 후속).
    const quotedBodyEl = wrapper.querySelector(`.${REGION_CLASSES.quotedBody}`)
    if (quotedBodyEl == null || !this.quotedBodyHasContentCached(core.view, quotedBodyEl)) {
      if (this.quotedToggle != null) {
        this.quotedToggle.cleanup()
        this.quotedToggle = null
        this.quotedToggleWrapper = null
      }
      return
    }

    // wrapper 가 바뀐 (requestRemount 후 새 wrapper) 경우 기존 토글 폐기 후 재부착.
    if (this.quotedToggle != null && this.quotedToggleWrapper !== wrapper) {
      this.quotedToggle.cleanup()
      this.quotedToggle = null
      this.quotedToggleWrapper = null
    }

    if (this.quotedToggle == null) {
      const toggle = createQuotedToggleButton(core.view, this.mailRegionLabels)
      wrapper.appendChild(toggle.btn)
      this.quotedToggle = toggle
      this.quotedToggleWrapper = wrapper
    } else {
      this.quotedToggle.sync()
    }
  }

  /**
   * quotedBodyHasContent 의 노드 참조 캐시 게이트 (OMD-233 후속 — 키당 ~4.6ms + trim 사본 GC 제거).
   *
   * quoted_body 는 스키마상 항상 doc 의 마지막 직속 자식이므로 `doc.lastChild` 참조 비교(O(1))로
   * 내용 불변을 판정한다. 캐시 대상은 **hasContent 불리언뿐** — ensureQuotedToggle 의 토글
   * mount/sync 로직은 doc 이 같아도 wrapper 가 바뀌는 구간(requestRemount 직후)이 있어 게이트하면
   * 토글 재부착이 누락된다. quoted_body **내부** 편집은 매 키 노드 참조가 바뀌어 캐시 미스가
   * 나지만 기존(매 키 재계산)과 동일 비용이라 회귀가 아니다.
   *
   * 전제: NodeView contentDOM 은 PM 노드 내용에서만 파생된다 — quoted_body 내부에 media 를
   * 주입하는 decoration 이 생기면 이 전제가 깨지므로 그때는 캐시 무효화 조건을 보강할 것.
   */
  private quotedBodyHasContentCached(view: EditorView, quotedBodyEl: Element): boolean {
    const last = view.state.doc.lastChild
    const quotedNode = last != null && last.type.name === 'quoted_body' ? last : null
    if (quotedNode != null && this.quotedBodyContentCache?.node === quotedNode) {
      return this.quotedBodyContentCache.hasContent
    }
    // lazy-collapsed-quoted-render: 접힘 생성 뷰는 contentDOM 이 없어 DOM 판정(textContent)이
    // 항상 false 가 되므로, 노드가 있으면 PM doc 기반 판정을 우선한다(토글 노출 유지).
    // 노드를 못 찾는 비정형 배치에서만 기존 DOM 판정으로 폴백.
    const hasContent =
      quotedNode != null ? quotedBodyNodeHasContent(quotedNode) : quotedBodyHasContent(quotedBodyEl)
    this.quotedBodyContentCache = quotedNode != null ? { node: quotedNode, hasContent } : null
    return hasContent
  }

  /**
   * paste 이벤트 listener 직접 등록 (2026-05-19 보강).
   *
   * **배경**: NgwEditor 0.2.10 의 `<Editor onPaste>` prop 이 React 차원에서 발화되지 않는 회귀
   * 확인됨 (사용자 보고 — onEditorPaste 핸들러가 호출되지 않음 + 콘솔 로그 미발생). 이를
   * 우회하기 위해 PM contenteditable element 에 capture phase 의 native paste 이벤트 listener
   * 를 직접 등록. listener 가 adapter handler 의 `onEditorPaste` 로 forward.
   *
   * capture phase 사용 이유: PM 의 자체 paste 처리 (handleDOMEvents.paste) 보다 먼저 실행되어
   * preventDefault 로 PM 동작을 차단할 수 있어야 cid/blob 변환 후 직접 insertHTML 흐름이 작동.
   */
  private attachPasteListener(dom: HTMLElement): void {
    // 멱등 — 이미 등록되어 있으면 먼저 해제.
    this.detachPasteListener()
    const listener = (event: ClipboardEvent): void => {
      this.adapter.getHandler()?.onEditorPaste?.(event)
    }
    dom.addEventListener('paste', listener, { capture: true })
    this.pasteListenerTarget = dom
    this.pasteListener = listener
  }

  private detachPasteListener(): void {
    if (this.pasteListenerTarget != null && this.pasteListener != null) {
      this.pasteListenerTarget.removeEventListener('paste', this.pasteListener, { capture: true })
    }
    this.pasteListenerTarget = null
    this.pasteListener = null
  }

  /**
   * `.ngw-editor` wrapper 의 좌/우 padding 클릭 시 focus 보강 (2026-05-21).
   *
   * **배경**: `.ngw-editor` 가 `padding: 16px` 을 가지며, 실제 contenteditable 인 `.ProseMirror`
   * 는 그 안쪽 자식 요소다. 사용자가 wrapper 의 padding 영역을 클릭하면 click target 이
   * `.ngw-editor` 자체가 되어 PM 으로 focus 가 이동하지 않는다.
   *
   * `@ngweditor/react` 내부의 `setupBottomClickCaret` 가 상단(첫 줄 위) / 하단(마지막 줄 아래)
   * 클릭은 GapCursor 로 처리하지만, **좌/우 padding** (또는 줄 사이 vertical gap 의 padding)
   * 클릭은 `if (event.clientY <= refRect.bottom) return` 으로 무시한다. 본 listener 가 그
   * 빈틈을 메운다.
   *
   * **동작**:
   *   - 좌클릭 (button === 0) 만 처리
   *   - 라이브러리 핸들러가 이미 처리해 `defaultPrevented` 면 skip
   *   - click target 이 wrapper 자체가 아니면 PM 이 처리하므로 skip
   *   - `view.posAtCoords` 로 가장 가까운 PM 위치 산출 후 selection + focus
   */
  private attachWrapperClickListener(dom: HTMLElement): void {
    this.detachWrapperClickListener()
    const wrapper = dom.parentElement
    if (wrapper == null || !wrapper.classList.contains('ngw-editor')) {
      return
    }
    const listener = (event: MouseEvent): void => {
      if (event.button !== 0) return
      if (event.defaultPrevented) return
      if (event.target !== wrapper) return

      const core = this.cachedCore
      if (core == null) return
      const view = core.view

      const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
      if (coords != null) {
        const resolved = view.state.doc.resolve(coords.pos)
        view.dispatch(view.state.tr.setSelection(Selection.near(resolved)))
      }
      view.focus()
      event.preventDefault()
    }
    wrapper.addEventListener('mousedown', listener)
    this.wrapperClickListenerTarget = wrapper
    this.wrapperClickListener = listener
  }

  private detachWrapperClickListener(): void {
    if (this.wrapperClickListenerTarget != null && this.wrapperClickListener != null) {
      this.wrapperClickListenerTarget.removeEventListener('mousedown', this.wrapperClickListener)
    }
    this.wrapperClickListenerTarget = null
    this.wrapperClickListener = null
  }

  /**
   * 편집 중 `<a href>` 클릭 시 링크가 브라우저로 열리는 것을 차단 (2026-09-09, OMD).
   *
   * **원인 (실측 확정)**: ngweditor 링크 mark 는 `target="_blank"` 로 렌더된다. Tauri/wry 는
   * `_blank` 링크를 WKWebView UI delegate(new-window) 경로로 OS 기본 브라우저에 자동으로 연다.
   * 이 경로는 메인 윈도우의 `on_navigation` 핸들러(src-tauri)를 타지 않으며, DOM `click` 의
   * `preventDefault()` 로도 취소되지 않는다. 그래서 편집 중 링크를 클릭하면 링크 quickmenu 와
   * 함께 브라우저까지 열렸다. 일반 브라우저(om-web-app)는 contenteditable 안의 링크 클릭에서
   * 네비게이션 자체가 일어나지 않아 이 문제가 없다.
   *
   * **차단 방식 (2단계)**:
   *   1. `mousedown` (capture) — 클릭 대상 anchor 의 `target`/`rel` 을 제거해 `_blank` 네이티브
   *      new-window 경로를 무력화(= same-frame 링크로 강등)한다. caret 배치를 위해 여기서는
   *      `preventDefault` 하지 않는다. PM 이 다음 렌더에서 `target` 을 되살리지만 클릭마다 다시
   *      제거되므로 상호작용 시점 기준으로 항상 유효하다.
   *   2. `click`/`auxclick` (capture) — 강등된 same-frame 링크의 기본 네비게이션을 `preventDefault`
   *      로 취소한다(이 경로는 `on_navigation` 으로 가지만 애초에 도달 전에 취소된다).
   *
   * `stopPropagation` 은 하지 않는다 — ngweditor 의 링크 quickmenu(편집/삭제)와 PM selection 처리는
   * 그대로 동작해야 한다.
   */
  private attachLinkClickGuard(dom: HTMLElement): void {
    this.detachLinkClickGuard()
    const findAnchor = (event: MouseEvent): HTMLAnchorElement | null =>
      event
        .composedPath()
        .find(
          (n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement && n.hasAttribute('href'),
        ) ?? null
    const listener = (event: MouseEvent): void => {
      const anchor = findAnchor(event)
      if (anchor == null) {
        return
      }
      if (event.type === 'mousedown') {
        // _blank 네이티브 new-window 경로 무력화. caret 배치를 위해 preventDefault 하지 않는다.
        anchor.removeAttribute('target')
        anchor.removeAttribute('rel')
        return
      }
      // 강등된 same-frame 링크의 기본 네비게이션 취소.
      event.preventDefault()
    }
    dom.addEventListener('mousedown', listener, { capture: true })
    dom.addEventListener('click', listener, { capture: true })
    dom.addEventListener('auxclick', listener, { capture: true })
    this.linkClickGuardTarget = dom
    this.linkClickGuardListener = listener
  }

  private detachLinkClickGuard(): void {
    if (this.linkClickGuardTarget != null && this.linkClickGuardListener != null) {
      this.linkClickGuardTarget.removeEventListener('mousedown', this.linkClickGuardListener, {
        capture: true,
      })
      this.linkClickGuardTarget.removeEventListener('click', this.linkClickGuardListener, {
        capture: true,
      })
      this.linkClickGuardTarget.removeEventListener('auxclick', this.linkClickGuardListener, {
        capture: true,
      })
    }
    this.linkClickGuardTarget = null
    this.linkClickGuardListener = null
  }

  /**
   * 에디터/플로팅 메뉴 **바깥**을 클릭하면 selection 을 collapse 한다 (OMD-228).
   *
   * 이미지 NodeSelection·표 CellSelection 등이 풀리면 그에 딸린 ngweditor quick menu·selection
   * 하이라이트와 커스텀 ImageToolbar(imageSelection plugin → `setSelectedImage(null)`)가 모두
   * 자동으로 닫힌다. "바깥" = 에디터 컨테이너(`.ngw-editor`/`.ngw-editor-container`)도, 플로팅
   * 메뉴(`[data-ngw-portal]`·`[data-ngw-block-ui]`·`[data-radix-popper-content-wrapper]`)도 아닌 곳.
   *
   * 에디터가 (a) 메인 문서 inline(platform), (b) 팝업/별도 document, (c) iframe(web) 중 어디에
   * 있든 동작하도록 **에디터 document 와 lifecycle 이 도는 document 양쪽**에 등록한다(중복 제거).
   * capture phase 로 settings 팝업 등이 열리기 전에 처리한다. selection 변경은
   * `addToHistory:false` 로 undo 히스토리를 오염시키지 않는다.
   */
  private attachOutsideSelectionClear(dom: HTMLElement): void {
    this.detachOutsideSelectionClear()
    const listener = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (
        target.closest(
          '.ngw-editor, .ngw-editor-container, [data-ngw-portal], [data-ngw-block-ui], [data-radix-popper-content-wrapper]',
        ) != null
      ) {
        return
      }
      const core = this.cachedCore
      if (core == null) return
      const view = core.view
      const state = view.state
      const tr = state.tr
      let changed = false

      // (1) 표 overlay(퀵메뉴·행/열 핸들)가 떠 있는지와 표 위치를 에디터 자신의 interaction store 로
      // 판정한다(ancestor-walk 추측보다 신뢰적). 표 overlay 는 `getCellRefFromSelection(state)` 가
      // non-null 인 동안(= 캐럿/CellSelection/표 전체선택이 표 안) 유지되므로(소스맵 interactionView.ts),
      // 캐럿을 표 **바로 앞**으로 빼 selection 재평가가 overlay 를 숨기게 하고, store 도 즉시 숨김
      // 처리해(pointer-sourced 로 남는 경우 대비) 메뉴를 확실히 내린다.
      const store = getTableInteractionStore(core) as
        | {
            getSnapshot?: () => { visible?: boolean; anchor?: { tablePos?: number } | null }
            setSnapshot?: (next: { visible: boolean; anchor: null }) => void
          }
        | undefined
      const snapshot = store?.getSnapshot?.()
      const tablePos = snapshot?.visible === true ? snapshot.anchor?.tablePos : undefined
      if (typeof tablePos === 'number') {
        // 캐럿을 표 **밖**으로 뺀다. 표 바로 앞(bias -1)을 우선하되, 앞에 둘 자리가 없어 첫 셀로
        // 빨려 들어가면(near 가 전방으로 fall) 표 바로 뒤(bias +1)로 보낸다. 표 안에 남으면
        // 하이라이트/overlay 가 유지되고 첫 셀이 선택되는 회귀가 생긴다.
        const tableNode = (
          state.doc as unknown as { nodeAt(pos: number): { nodeSize: number } | null }
        ).nodeAt(tablePos)
        const tableEnd = tablePos + (tableNode?.nodeSize ?? 1)
        let nextSelection = Selection.near(state.doc.resolve(tablePos), -1)
        if (nextSelection.from >= tablePos && nextSelection.from < tableEnd) {
          const afterPos = Math.min(tableEnd, state.doc.content.size)
          nextSelection = Selection.near(state.doc.resolve(afterPos), 1)
        }
        tr.setSelection(nextSelection)
        changed = true
      } else if (!state.selection.empty) {
        // 비-표(이미지 NodeSelection·텍스트 범위)는 제자리 collapse.
        tr.setSelection(Selection.near(state.doc.resolve(state.selection.from)))
        changed = true
      }

      // (2) 행/열 핀 하이라이트(`pinnedHighlight` decoration)는 document selection 이 아니라 plugin
      // state 라 selection 이동으로 안 풀린다 — apply 가 읽는 meta 로 직접 null 리셋(소스맵
      // plugin/interactionPlugin.ts: `tr.getMeta(tableInteractionPluginKey)` 의 hovered/pinnedHighlight).
      const tableState = tableInteractionPluginKey.getState(state) as
        | { hovered?: unknown; pinnedHighlight?: unknown }
        | null
        | undefined
      if (
        tableState != null &&
        (tableState.hovered != null || tableState.pinnedHighlight != null)
      ) {
        tr.setMeta(tableInteractionPluginKey, { hovered: null, pinnedHighlight: null })
        changed = true
      }

      if (changed) {
        tr.setMeta('addToHistory', false)
        view.dispatch(tr)
      }
      // dispatch(=selection 재평가) 후에도 pointer-sourced overlay 가 남을 수 있으므로 store 를
      // 확실히 숨긴다. 캐럿이 표 밖으로 나갔으니 이후 pointermove 가 다시 띄우지 않는다.
      if (typeof tablePos === 'number') {
        store?.setSnapshot?.({ visible: false, anchor: null })
      }
    }
    const docs = Array.from(new Set<Document>([dom.ownerDocument, document]))
    for (const d of docs) {
      d.addEventListener('pointerdown', listener, { capture: true })
    }
    this.outsideSelectionClearDocs = docs
    this.outsideSelectionClearListener = listener
  }

  private detachOutsideSelectionClear(): void {
    if (this.outsideSelectionClearDocs != null && this.outsideSelectionClearListener != null) {
      for (const d of this.outsideSelectionClearDocs) {
        d.removeEventListener('pointerdown', this.outsideSelectionClearListener, { capture: true })
      }
    }
    this.outsideSelectionClearDocs = null
    this.outsideSelectionClearListener = null
  }

  /**
   * eventBridge extension 의 setup 콜백이 호출 — PR-N6. PM view 보관 시점에 초기 스냅샷도 기록한다.
   * @internal
   */
  attachEventBridgeView(view: EditorView): void {
    this.bridgeView = view
    this.lastSelectionCollapsed = view.state.selection.empty
    this.lastFormatSnapshot = this.captureFormatSnapshot(view)
  }

  /**
   * selection 변경을 300ms 디바운싱 후 발화. 디바운싱 위치는 사용자 결정 #2 — lifecycle 인스턴스
   * 멤버. dispose 가 clearTimeout 으로 일괄 cleanup.
   */
  private scheduleSelectionChangedCheck(): void {
    const win = this.getSafeWindow()
    if (win == null) {
      return
    }
    if (this.selectionChangeTimer != null) {
      win.clearTimeout(this.selectionChangeTimer)
    }
    this.selectionChangeTimer = win.setTimeout(() => {
      this.selectionChangeTimer = null
      this.detectSelectionChanged()
    }, SELECTION_CHANGE_DEBOUNCE_MS)
  }

  private detectSelectionChanged(): void {
    if (this.bridgeView == null) {
      return
    }
    const collapsed = this.bridgeView.state.selection.empty
    if (collapsed === this.lastSelectionCollapsed) {
      return
    }
    this.lastSelectionCollapsed = collapsed
    this.adapter.getHandler()?.onSelectionChanged?.(collapsed)
  }

  private detectFormatChanged(): void {
    if (this.bridgeView == null) {
      return
    }
    const next = this.captureFormatSnapshot(this.bridgeView)
    const handler = this.adapter.getHandler()
    if (handler?.formatChanged == null) {
      this.lastFormatSnapshot = next
      return
    }
    const previous = this.lastFormatSnapshot
    const keys = new Set<string>([...Object.keys(previous), ...Object.keys(next)])
    for (const name of keys) {
      const before = previous[name]
      const after = next[name]
      if (before === after) {
        continue
      }
      const payload: FormatChangePayload = { name, state: after ?? false }
      handler.formatChanged(payload)
    }
    this.lastFormatSnapshot = next
  }

  /**
   * 현재 selection 의 active marks 를 (markName → state) 사전으로 캡처. bool 만 보관 — 향후 색/폰트
   * attr 보강 시 string state 도 함께 보관할 여지를 둔다.
   */
  private captureFormatSnapshot(view: EditorView): Record<string, boolean | string> {
    const snapshot: Record<string, boolean | string> = {}
    const { state } = view
    const marks: readonly Mark[] = state.storedMarks ?? state.selection.$from.marks()
    for (const mark of marks) {
      snapshot[mark.type.name] = true
    }
    return snapshot
  }

  private getSafeWindow(): Window | null {
    try {
      return this.adapter.host.getWindow()
    } catch {
      return null
    }
  }
}
